import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/i18nContext';
import { ROLE_LABELS, ROLES } from '../../constants/roles';
import ProfileDrawer from '../../components/ProfileDrawer';
import Button from '../../components/ui/Button';
import '../../styles/UsersPage.css';

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString([], {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const RoleBadge = ({ role }) => {
  return (
    <span className={`role-badge role-badge--${role}`}>
      {ROLE_LABELS[role] ?? `Role ${role}`}
    </span>
  );
};

// Valóban zárolt-e most?
const isCurrentlyLocked = (u) =>
  u.locked_until && new Date(u.locked_until) > new Date();

const UsersPage = () => {
  const { api, user: currentUser } = useAuth();
  const { t } = useI18n();

  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [search, setSearch]     = useState('');

  const [drawerUserId, setDrawerUserId] = useState(null);

  const [selected, setSelected]     = useState(new Set());
  const [bulkRole, setBulkRole]     = useState('2');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMsg, setBulkMsg]       = useState('');
  const [bulkPw, setBulkPw]         = useState('');
  const [showBulkPw, setShowBulkPw] = useState(false);

  const loadUsers = useCallback(() => {
    setLoading(true);
    api.get('/admin/users')
      .then(r => setUsers(r.data.users))
      .catch(e => setError(e.response?.data?.error || t('common.error')))
      .finally(() => setLoading(false));
  }, [api, t]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const filtered = users.filter(u =>
    u.deleted_at == null &&
    [u.first_name, u.last_name, u.email, u.username]
      .join(' ').toLowerCase()
      .includes(search.toLowerCase())
  );

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(u => u.id)));
  };

  const doBulk = async (action, value) => {
    const userIds = [...selected];
    if (userIds.length === 0) return;
    setBulkLoading(true);
    setBulkMsg('');
    try {
      const payload = { action, userIds };
      if (value !== undefined) payload.value = value;
      const r = await api.post('/admin/users/bulk', payload);
      setBulkMsg(`✅ ${r.data.affected} ${t('admin.modified')}`);
      setSelected(new Set());
      setBulkPw('');
      loadUsers();
    } catch (e) {
      const errMsg = e.response?.data?.error || '';
      if (errMsg.toLowerCase().includes('weak')) {
        setBulkMsg(`❌ ${t('admin.passwordWeak')}`);
      } else {
        setBulkMsg(`❌ ${errMsg || t('common.error')}`);
      }
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkPassword = () => {
    if (!bulkPw) return;
    const strongPw = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/.test(bulkPw);
    if (!strongPw) { setBulkMsg(`❌ ${t('admin.passwordWeak')}`); return; }
    if (!window.confirm(`${t('admin.bulkSetPassword')} — ${selected.size} user?`)) return;
    doBulk('setPassword', bulkPw);
  };

  // Gyors unlock egyetlen sorra (ne kelljen profil oldalra menni)
  const handleQuickUnlock = async (e, userId) => {
    e.stopPropagation();
    try {
      await api.put(`/admin/users/${userId}/unlock`);
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, locked_until: null, failed_attempts: 0 } : u
      ));
    } catch { /* silent */ }
  };

  const isSuperadmin = currentUser?.role === ROLES.SUPERADMIN;

  // Kijelölt users között van-e legalább egy zárolt?
  const selectedHasLocked = [...selected].some(id => {
    const u = users.find(x => x.id === id);
    return u && isCurrentlyLocked(u);
  });

  return (
    <div className="users-page">
      {/* HEADER */}
      <div className="users-header">
        <h2 className="users-title">👥 {t('admin.users')}</h2>
        <div className="users-header-right">
          <input
            type="text"
            placeholder={t('admin.searchUsers')}
            value={search}
            onChange={e => { setSearch(e.target.value); setSelected(new Set()); }}
            className="users-search"
          />
          <span className="users-count">{filtered.length} / {users.filter(u => !u.deleted_at).length}</span>
        </div>
      </div>

      {/* BULK TOOLBAR — always rendered to prevent layout jump */}
      <div className={`bulk-toolbar${selected.size === 0 ? ' bulk-toolbar--empty' : ''}`}>
        <div className="bulk-toolbar-row">
            <span className="bulk-count">{selected.size} {t('admin.selected')}</span>
            <div className="bulk-actions">
              <Button variant="outline" size="sm" onClick={() => doBulk('activate')}   disabled={bulkLoading}>✅ {t('admin.activate')}</Button>
              <Button variant="ghost"   size="sm" onClick={() => doBulk('deactivate')} disabled={bulkLoading}>⏸ {t('admin.deactivate')}</Button>
              {selectedHasLocked && (
                <Button variant="outline" size="sm" onClick={() => doBulk('unlock')} disabled={bulkLoading}>
                  🔓 {t('admin.bulkUnlock')}
                </Button>
              )}
              <div className="bulk-role-group">
                <select value={bulkRole} onChange={e => setBulkRole(e.target.value)} className="bulk-role-select" disabled={bulkLoading}>
                  {isSuperadmin && <option value="0">Superadmin</option>}
                  <option value="1">Admin</option>
                  <option value="2">User</option>
                  <option value="3">Agent</option>
                </select>
                <Button variant="outline" size="sm" onClick={() => doBulk('setRole', Number(bulkRole))} disabled={bulkLoading}>
                  🎭 {t('admin.changeRole')}
                </Button>
              </div>
              <Button variant="danger" size="sm" onClick={() => { if (window.confirm(`${t('admin.delete')} ${selected.size}?`)) doBulk('delete'); }} disabled={bulkLoading}>
                🗑️ {t('admin.delete')}
              </Button>
            </div>
          </div>

          {/* BULK JELSZÓCSERE SOR */}
          <div className="bulk-pw-row">
            <span className="bulk-pw-label">🔑</span>
            <div className="bulk-pw-input-wrap">
              <input
                type={showBulkPw ? 'text' : 'password'}
                className="bulk-pw-input"
                placeholder={t('admin.bulkPasswordPlaceholder')}
                value={bulkPw}
                onChange={e => { setBulkPw(e.target.value); setBulkMsg(''); }}
                disabled={bulkLoading}
                autoComplete="new-password"
              />
              <button type="button" className="bulk-pw-eye" onClick={() => setShowBulkPw(s => !s)}>
                {showBulkPw ? '🙈' : '👁️'}
              </button>
            </div>
            <Button variant="solid" size="sm" onClick={handleBulkPassword} disabled={bulkLoading || !bulkPw}>
              {t('admin.bulkSetPassword')}
            </Button>
            <span className="bulk-pw-hint">{t('admin.passwordHint')}</span>
          </div>

          {bulkMsg && <span className="bulk-msg">{bulkMsg}</span>}
        </div>

      {loading && <div className="admin-loading">{t('common.loading')}</div>}
      {error   && <div className="admin-error">⚠️ {error}</div>}

      {!loading && !error && (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="col-check">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleAll}
                  />
                </th>
                <th>ID</th>
                <th>{t('auth.firstName')} / {t('auth.lastName')}</th>
                <th>{t('auth.username')}</th>
                <th>{t('admin.role')}</th>
                <th>{t('admin.status')}</th>
                <th>{t('auth.email')} ✓</th>
                <th>{t('admin.lastLogin')}</th>
                <th>{t('admin.registered')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={10} className="admin-empty">{t('admin.noResults')}</td></tr>
              ) : filtered.map(u => {
                const locked = isCurrentlyLocked(u);
                return (
                  <tr
                    key={u.id}
                    className={`${selected.has(u.id) ? 'row-selected' : ''} ${locked ? 'row-locked' : ''}`}
                    onClick={() => setDrawerUserId(u.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="col-check" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(u.id)}
                        onChange={() => toggleSelect(u.id)}
                      />
                    </td>
                    <td className="cell-id">#{u.id}</td>
                    <td>
                      <div className="user-name">
                        {[u.first_name, u.last_name].filter(Boolean).join(' ') || '—'}
                      </div>
                      <div className="user-email">{u.email}</div>
                    </td>
                    <td>{u.username ?? '—'}</td>
                    <td><RoleBadge role={u.role} /></td>
                    <td>
                      <div className="status-cell">
                        <span className={`status-badge ${u.is_active ? 'active' : 'inactive'}`}>
                          <span className="status-dot" />
                          {u.is_active ? t('admin.active') : t('admin.inactive')}
                        </span>
                        {locked && (
                          <span
                            className="lock-badge"
                            title={`${t('admin.lockedUntil')}: ${formatDate(u.locked_until)} | ${t('admin.failedAttempts')}: ${u.failed_attempts}`}
                          >
                            🔒
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="verified-badge">{u.email_verified ? '✅' : '❌'}</td>
                    <td className="cell-date">{formatDate(u.last_login)}</td>
                    <td className="cell-date">{formatDate(u.created_at)}</td>
                    <td onClick={e => e.stopPropagation()}>
                      {locked && (
                        <Button variant="outline" size="sm"
                          onClick={(e) => handleQuickUnlock(e, u.id)}
                          title={t('admin.unlock')}>
                          🔓
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => setDrawerUserId(u.id)}>
                        {t('admin.viewProfile')}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {drawerUserId && (
        <ProfileDrawer
          userId={drawerUserId}
          onClose={() => { setDrawerUserId(null); loadUsers(); }}
        />
      )}
    </div>
  );
};

export default UsersPage;
